import { useLocale } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { formatNumber } from "@/shared/lib/format";

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  /** Shown after the label, in the locale's digits. */
  count?: number;
}

interface SegmentedControlProps<T extends string> {
  /** Names the group for screen readers. */
  label: string;
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** "md" for filters inside a toolbar; "lg" for a page's main views (as on My work). */
  size?: "md" | "lg";
  className?: string;
}

/**
 * A row of toggle buttons where exactly one is pressed: filters and view switches. On narrow
 * screens it scrolls sideways instead of wrapping.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  size = "md",
  className,
}: SegmentedControlProps<T>) {
  const large = size === "lg";
  const { locale } = useLocale();
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        "inline-flex w-fit max-w-full self-start overflow-x-auto bg-surface-sunken [scrollbar-width:none]",
        large ? "gap-1 rounded-2xl border border-line p-1" : "gap-0.5 rounded-lg p-0.5",
        className,
      )}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => {
            onChange(option.value);
          }}
          className={cn(
            "flex shrink-0 items-center gap-1.5 font-medium whitespace-nowrap transition-colors",
            large
              ? "h-9 gap-2 rounded-xl px-3.5 text-[14.5px]"
              : "h-7 rounded-md px-2.5 text-[13.5px]",
            value === option.value
              ? cn("bg-surface text-ink ring-1 ring-line", large ? "shadow-sm" : "shadow-xs")
              : "text-ink-muted hover:text-ink",
          )}
        >
          {option.label}
          {option.count === undefined ? null : large ? (
            <span
              className={cn(
                "inline-flex h-5.5 min-w-5.5 items-center justify-center rounded-full px-1.5 text-[12px] font-semibold tabular-nums",
                value === option.value
                  ? "bg-brand-soft text-brand-strong"
                  : "bg-surface text-ink-muted",
              )}
            >
              {formatNumber(option.count, locale)}
            </span>
          ) : option.count === 0 ? null : (
            // A bare Arabic zero reads as a stray dot, so an empty option shows no count.
            <span className="text-[12.5px] text-ink-subtle tabular-nums">
              {formatNumber(option.count, locale)}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
